import React from 'react'
import { Navigate } from 'react-router-dom'
import { ROLES } from '../../constants/roles'
import { useRole } from '../../hooks/auth/useRole'
import Loading from '../../pages/Loading'

interface RoleGuardProps {
  /** Roles permitted to view the children. */
  allow: ROLES[]
  /** Where to redirect when the current role is not permitted. Defaults to the user home. */
  redirect?: string
  /** Pages showing colleagues' pay: closed to accounts without pay access. */
  requirePayAccess?: boolean
  children: React.ReactNode
}

/**
 * Client-side role guard for routes. This is a UX convenience only — the
 * backend enforces permissions server-side.
 *
 * Uses the same resolved role as the sidebar (useRole) rather than reading
 * localStorage directly. The two used to be separate sources for one decision,
 * so they could disagree: the sidebar reads the role the backend reports, while
 * localStorage 'role' can be stale (left at 'user' by the admin/user preview
 * switch) or briefly absent (a 401 clears it, and it is only rewritten by an
 * effect that runs after the routes have already rendered). Whenever they
 * diverged the nav still offered a section that this guard then bounced
 * straight back to the home page.
 *
 * useRole keeps the preview switch working: previewing as a regular user
 * reports role 'user', which hides those sections from the sidebar and blocks
 * them here, together.
 */
const RoleGuard = ({ allow, redirect = '/user/home', requirePayAccess = false, children }: RoleGuardProps) => {
  const { role, isRoleResolved, hidesPayAndContracts } = useRole()

  // Deciding before the role is known is what produced the bounce: the guard
  // would redirect on the fallback value and the redirect wins, even though the
  // real role arrives a moment later.
  if (!isRoleResolved) return <Loading />

  const allowed = (allow as string[]).includes(role) && !(requirePayAccess && hidesPayAndContracts)
  return allowed ? <>{children}</> : <Navigate to={redirect} replace />
}

export default RoleGuard
